// ============================================
// SET UP NEW HOOKS
// Combines hooks from:
// - [SetUpNew.tsx] -> SetUpNew (SET UP NEW 524)
// - [NewPlan.tsx] -> SetUpNew (NEW PLAN 500-501)
// - [NewPlanForm.tsx] -> [NewPlan.tsx] -> SetUpNew (NEW PLAN FORM 502-526)
// - [NewPlanHelpers.tsx] -> [NewPlanForm.tsx] -> SetUpNew (NEW PLAN HELPERS 503-518)
// ============================================

import React, {useEffect, useMemo, useState} from 'react';
import {GetProductProviders} from '../../../schema-types/GetProductProviders';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {SelectGroupOption, SelectOption} from '../../../components/AutoForm/AutoFieldNew';
import {
  SetFieldValueWithSourceKey,
  AutoFieldHelpers,
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {mainPlanTypeType, recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {
  newPlanFields,
  newSubplanFields,
  setUpNewFields,
} from '../RecommendsSetUpNew/setUpNewFields';
import type {modelPortfolioField} from '../RecommendModify/modifyFieldTypes';
import {defaultFeeFields, getPlanFeesFormikFromServer} from '../CreateRecommend/PlanFees';
import {optionalRound} from '../CreateRecommend/RecommendSourceDestination/utils';
import {includesSaltus} from '../../../utils/includesSaltus';
import {getEnvironment} from '../../../utils/envTest';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {UPDATES} from '../../../shared/UPDATES';

// ============================================
// NewPlanHelpers.tsx (503, 505-518)
// ============================================

export const updateNewPlanProducts = ({
  productNames,
  setFieldValue,
  formikKeyPrefix,
  setDisplayProductNames,
  minor,
  currentPlanId,
  mainPlanType,
}: {
  minor?: boolean;
  mainPlanType: mainPlanTypeType;
  formikKeyPrefix: string;
  productNames?: SelectOption[];
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  setDisplayProductNames: React.Dispatch<
    React.SetStateAction<SelectOption[] | undefined>
  >;
  currentPlanId: string | null;
}) => {
  if (!productNames) {
    return;
  }
  const filteredProducts = [
    ...productNames.filter((x) => (minor ? x.label !== 'ISA' : x.label !== 'J-ISA')),
  ]
    .filter((a) => {
      if (!minor && mainPlanType === 'isa') {
        return !a.label.toLocaleLowerCase().includes('junior');
      }
      if (minor && mainPlanType === 'isa') {
        return a.label.toLocaleLowerCase().includes('junior');
      }

      return true;
    })
    .sort((a, b) => {
      if (a.label.toLocaleLowerCase().includes('self')) {
        return -1;
      }

      if (b.label.toLocaleLowerCase().includes('self')) {
        return 1;
      }

      if (a.label.toLocaleLowerCase().includes('junior')) {
        return 1;
      }
      if (b.label.toLocaleLowerCase().includes('junior')) {
        return -1;
      }
      return 0;
    });

  const envName = getEnvironment();
  if (envName === 'PROD') {
    // ON-3342 - Remove LISA temporarily
    filteredProducts.filter((x) => !x.label.toLocaleLowerCase().includes('lifetime'));
  }
  if (filteredProducts.length < 1) {
    return;
  }

  setDisplayProductNames(filteredProducts);

  // refreshing selection on mainPlanType change if currentPlan isnt in list
  if (!currentPlanId || !filteredProducts.find((x) => x.value === currentPlanId)) {
    setFieldValue(UPDATES._503, `${formikKeyPrefix}.planType`, undefined, true);
  }
};

export const updateNewPlanProviders = ({
  rawProviders,
  setProductProvidersOptionGroups,
}: {
  rawProviders: GetProductProviders | undefined;
  setProductProvidersOptionGroups: React.Dispatch<
    React.SetStateAction<SelectGroupOption[]>
  >;
}) => {
  if (!rawProviders?.productProviders) {
    return;
  }
  const items: Record<string, SelectOption[]> = {
    favourite: [],
    panel: [],
    other: [],
  };
  rawProviders.productProviders.forEach((x) => {
    const item = {
      value: x!.id,
      label: x.name! || x!.formattedName!,
    };
    if (x.isFavourite) {
      items.favourite.push(item);
    } else if (x.isPanel) {
      items.panel.push(item);
    } else {
      items.other.push(item);
    }
  });

  setProductProvidersOptionGroups([
    {
      groupLabel: 'Favourite',
      options: [...items.favourite].sort((a, b) => {
        // put saltus at the top
        if (includesSaltus(a.label)) {
          return -1;
        }
        return 0;
      }),
    },
    {groupLabel: 'Panel', options: items.panel},
    {groupLabel: 'Other', options: items.other},
  ]);
};

// ============================================
// NewPlan.tsx (500-501)
// ============================================

const getIsLowes = (
  selectedPlanProvider?: string,
  productProviders?: PlanProviderSelectOption[],
  mainPlanType?: mainPlanTypeType
) => {
  if (!selectedPlanProvider || !productProviders?.length || mainPlanType !== 'pension') {
    return false;
  }
  return Boolean(
    productProviders.find(
      (pp) =>
        pp.value === selectedPlanProvider &&
        pp.label.toLocaleLowerCase().includes('lowes')
    )
  );
};

export const getIsTransactBond = (
  selectedPlanProvider?: string,
  selectedPlanType?: string,
  productProviders?: PlanProviderSelectOption[],
  productNames?: SelectOption[],
  mainPlanType?: mainPlanTypeType
) => {
  if (
    !selectedPlanProvider ||
    !selectedPlanType ||
    !productProviders?.length ||
    !productNames?.length ||
    mainPlanType !== 'bond'
  ) {
    return false;
  }

  // Check if provider is Transact
  const isTransactProvider = Boolean(
    productProviders.find(
      (pp) =>
        pp.value === selectedPlanProvider &&
        pp.label.toLocaleLowerCase().includes('transact')
    )
  );

  // Check if product type is Investment Bond
  const isInvestmentBond = Boolean(
    productNames.find(
      (product) =>
        product.value === selectedPlanType &&
        product.label.toLocaleLowerCase().includes('investment bond')
    )
  );

  return isTransactProvider && isInvestmentBond;
};

export type NewPlanFormikPrefix =
  | 'setUpNew.newPlan'
  | 'contribute.newPlan'
  | 'transfer.newPlan';

export const useDeriveIsLowesPension = (formikKeyPrefix: NewPlanFormikPrefix) => {
  const {values} = useAutoForm<recommendFieldsType>();
  const [planProviderObj] = useAutoField<newPlanFields['planProvider']>(
    `${formikKeyPrefix}.planProvider`
  );
  const planNameProviderUtils = usePlanNamesProviders();
  const isLowesPension = useMemo(() => {
    return getIsLowes(
      planProviderObj.value,
      planNameProviderUtils.productProviders,
      values.mainPlanType
    );
  }, [
    planProviderObj.value,
    planNameProviderUtils.productProviders,
    values.mainPlanType,
  ]);
  return isLowesPension;
};

export const useDeriveIsTransactBond = (formikKeyPrefix: NewPlanFormikPrefix) => {
  const {values} = useAutoForm<recommendFieldsType>();
  const [planProviderObj] = useAutoField<newPlanFields['planProvider']>(
    `${formikKeyPrefix}.planProvider`
  );
  const [planTypeObj] = useAutoField<newPlanFields['planType']>(
    `${formikKeyPrefix}.planType`
  );
  const planNameProviderUtils = usePlanNamesProviders();
  const isTransactBond = useMemo(() => {
    return getIsTransactBond(
      planProviderObj.value,
      planTypeObj.value,
      planNameProviderUtils.productProviders,
      planNameProviderUtils.productNames,
      values.mainPlanType
    );
  }, [
    planProviderObj.value,
    planTypeObj.value,
    planNameProviderUtils.productProviders,
    planNameProviderUtils.productNames,
    values.mainPlanType,
  ]);
  return isTransactBond;
};

// ============================================
// NewPlanForm.tsx (502, 504, 525, 526)
// ============================================

export const updateIsSaltusProvider = ({
  isSaltusProviderHelpers,
  productProviders,
  planProvider,
}: {
  isSaltusProviderHelpers: AutoFieldHelpers<boolean>;
  productProviders: ReturnType<typeof usePlanNamesProviders>['productProviders'];
  planProvider: string | undefined;
}) => {
  isSaltusProviderHelpers.setValue(
    UPDATES._525,
    includesSaltus(
      productProviders?.find((x) => x.value === planProvider)?.label as string
    ),
    true
  );
};

export const deriveIsNewLisa = ({
  lisaId,
  planTypeFieldValue,
  setFieldValue,
}: {
  lisaId: string | undefined;
  planTypeFieldValue: string | undefined;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  const isLisa = !!lisaId && planTypeFieldValue === lisaId;
  setFieldValue(UPDATES._502, 'isLisa', isLisa);
  return isLisa;
};

export const setDefaultSaltusSippPlanType = ({
  displayProductNames,
  isSaltusSIPP,
  setFieldValue,
  formikKeyPrefix,
}: {
  displayProductNames: SelectOption[] | undefined;
  isSaltusSIPP: boolean;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  formikKeyPrefix: string;
}) => {
  if (!displayProductNames?.length) {
    return;
  }
  const defaultSaltusSippPlanType = displayProductNames.find(
    (x) => x.label.toLocaleLowerCase() === 'self invested personal pension'
  );
  if (defaultSaltusSippPlanType && isSaltusSIPP) {
    setFieldValue(
      UPDATES._504,
      `${formikKeyPrefix}.planType`,
      defaultSaltusSippPlanType.value
    );
  }
};

export const clearMandateOnProviderBlur = ({
  mandateHelpers,
}: {
  mandateHelpers: AutoFieldHelpers<modelPortfolioField | undefined>;
}) => {
  mandateHelpers.setValue(UPDATES._526, undefined, true);
};

// ============================================
// SetUpNew.tsx (524)
// ============================================

export const loadSavedSetUpNewPlan = async ({
  recommendDetails,
  setUpNewFieldHelpers,
}: {
  recommendDetails: OVRecommendation | null;
  setUpNewFieldHelpers: AutoFieldHelpers<setUpNewFields['newPlan'] | undefined>;
}) => {
  if (recommendDetails) {
    const parentPot = recommendDetails.pots?.find((x) => !!x.pot && !x.pot.parent);
    if (parentPot?.pot) {
      const model = parentPot?.pot?.modelPortfolio || parentPot?.pot?.mandate;
      const newPlanValues: newPlanFields = {
        existingPotId: parentPot.pot.id,
        planProvider: parentPot.pot.productProvider?.id,
        planType: parentPot.pot.productName?.id,
        inTrust: parentPot.pot.inTrust ?? undefined,
        model: model || undefined,
        planFees: getPlanFeesFormikFromServer(parentPot) || defaultFeeFields,
        contactId: parentPot.pot.contact?.id,
        parentGuardianId: recommendDetails.parentGuardian?.id,
      };
      const serverSubplans = (recommendDetails.pots || [])
        .filter((x) => !!x.pot?.parent)
        .sort((a, b) => {
          if (a.pot?.isSaltus) {
            return -1;
          }
          if (b.pot?.isSaltus) {
            return 1;
          }
          return 0;
        });

      serverSubplans.forEach((ovPot, i) => {
        if (!ovPot.pot) {
          return;
        }
        const planFees = getPlanFeesFormikFromServer(ovPot);
        const subplanModel = ovPot?.pot?.modelPortfolio || ovPot?.pot?.mandate;
        const subplanValues: newSubplanFields = {
          planFees,
          checked: true,
          existingPotId: ovPot.pot.id,
          parentId: ovPot.pot?.parent?.id,
          singleClientAmount: optionalRound(ovPot?.singleClientAmount) || '',
          planProvider: ovPot?.pot.productProvider?.id || undefined,
          productProviderId: ovPot?.pot.productProvider?.id || undefined,
          model: subplanModel || undefined,
        };
        if (i === 0) {
          newPlanValues.subplan0 = subplanValues;
        } else {
          newPlanValues.subplan1 = subplanValues;
        }
      });
      return setUpNewFieldHelpers.setValue(UPDATES._524, newPlanValues);
    }
  }
};
